import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import HtmlHead from 'components/html-head/HtmlHead';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import { ToastSuccess, ToastErrorCath } from 'components/alerts/toast';
import { changePassword } from 'api/Api-user';
import { setCurrentUser, setIsLoginUser } from 'slices/auth/authSlice';

const PASSWORD_RULES = [
  { label: '8 caracteres o más', test: (value) => value.length >= 8 },
  { label: 'Al menos un dígito', test: (value) => /\d/.test(value) },
  { label: 'Al menos una letra minúscula', test: (value) => /[a-z]/.test(value) },
  { label: 'Al menos una letra mayúscula', test: (value) => /[A-Z]/.test(value) },
  { label: 'Al menos un carácter especial: ! @ # $ % ^ & * ( ) _ +', test: (value) => /[!@#$%^&*()_+]/.test(value) },
];

const UNAVAILABLE_MESSAGE = 'El cambio de contraseña no está disponible por el momento. Inténtalo más tarde.';

const ChangePassword = () => {
  const title = 'Actualizar contraseña';
  const description = 'Actualizar contraseña Plataforma Ioenvio';
  const dispatch = useDispatch();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const rulesPassed = PASSWORD_RULES.every((rule) => rule.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit = currentPassword && rulesPassed && confirmPassword && passwordsMatch && !isSaving;

  const closeSession = () => {
    dispatch(setCurrentUser({}));
    dispatch(setIsLoginUser(false));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSaving(true);
    setErrors([]);
    try {
      const { status, data } = await changePassword(currentPassword, newPassword);
      const formErrors = data.error?.['change-password'];
      if (status === 200) {
        ToastSuccess(data.message);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else if (formErrors) {
        // 406 (validation) or 429 (too many attempts)
        setErrors(formErrors);
      } else if (status === 401 || data.error?.authentication) {
        closeSession();
      } else {
        // 404 means the endpoint is not deployed yet
        setErrors([UNAVAILABLE_MESSAGE]);
      }
    } catch (err) {
      ToastErrorCath();
    } finally {
      setIsSaving(false);
    }
  };

  const renderPasswordField = (id, label, value, onChange) => (
    <Form.Group className="mb-4" controlId={id}>
      <Form.Label className="text-muted text-small">{label}</Form.Label>
      <div className="input-group">
        <span className="input-group-text bg-transparent">
          <CsLineIcons icon="lock-on" size="16" />
        </span>
        <Form.Control type="password" autoComplete={id === 'current-password' ? 'current-password' : 'new-password'} value={value} onChange={onChange} />
      </div>
    </Form.Group>
  );

  return (
    <>
      <HtmlHead title={title} description={description} />
      <div className="page-title-container mb-3">
        <Row className="g-0">
          <Col className="col-auto mb-3 mb-sm-0 me-auto">
            <h1 className="mb-0 pb-0 display-4">{title}</h1>
          </Col>
        </Row>
      </div>

      <Row>
        <Col xs="12" lg="8">
          <Form onSubmit={handleSubmit}>
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-1">Cambia la contraseña de tu cuenta</h5>
                <p className="text-muted text-small mb-4">Tu sesión seguirá activa después del cambio.</p>

                {renderPasswordField('current-password', 'Contraseña actual', currentPassword, (e) => setCurrentPassword(e.target.value))}
                {renderPasswordField('new-password', 'Nueva contraseña', newPassword, (e) => setNewPassword(e.target.value))}

                <ul className="list-unstyled mb-4 text-small">
                  {PASSWORD_RULES.map((rule) => {
                    const passed = rule.test(newPassword);
                    return (
                      <li key={rule.label} className={passed ? 'text-success' : 'text-muted'}>
                        <CsLineIcons icon={passed ? 'check' : 'close'} size="12" className="me-1" />
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>

                {renderPasswordField('confirm-password', 'Confirmar nueva contraseña', confirmPassword, (e) => setConfirmPassword(e.target.value))}
                {confirmPassword && !passwordsMatch && <Form.Text className="text-danger d-block mt-n3 mb-4">Las contraseñas no coinciden.</Form.Text>}

                {errors.length > 0 && (
                  <Alert variant="danger" className="mb-0">
                    <ul className="mb-0 ps-3">
                      {errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </Card.Body>
            </Card>

            <div className="mb-5">
              <Button variant="primary" type="submit" disabled={!canSubmit}>
                {isSaving && (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Guardando...
                  </>
                )}
                {!isSaving && 'Actualizar contraseña'}
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default ChangePassword;
